import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import {
  CheckboxWithLabelComponent,
  FormControlComponent,
  NoDataPipe,
  PrimengButtonDirective,
  PrimengSelectDirective,
  SelectItem,
} from '@web/shared/ui';
import { InputTextModule } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { AccordionModule } from 'primeng/accordion';
import { CheckboxModule } from 'primeng/checkbox';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteModule,
} from 'primeng/autocomplete';
import { Select } from 'primeng/select';
import { WarehouseSkuUsageStatusComponent } from '../../containers/warehouse-sku-usage-status/warehouse-sku-usage-status.component';
import { debounceTime, Observable, startWith } from 'rxjs';
import { Guid, Nillable, valueOrDefault } from '@web/utils';
import { WarehouseReceiptProductFormGroup } from '../../models/warehouse-receipt-form-data';
import { WarehouseOwnerDataSourceService } from '../../services/warehouse-owner-data-source.service';
import { AdminBrandSelectItemsProviderService } from '@web/shared/module/brand';
import { WarehouseProductType } from '../../enums/warehouse-product-type';
import { WarehouseProductTypeProviderService } from '../../providers/warehouse-product-type-provider.service';
import { QuantityUnitProviderService } from '@web/shared/module/measurement';

@Component({
  selector: 'app-warehouse-receipt-form-product-panel',
  imports: [
    AccordionModule,
    AutoCompleteModule,
    Button,
    CheckboxModule,
    CheckboxWithLabelComponent,
    CommonModule,
    FormControlComponent,
    InputTextModule,
    NoDataPipe,
    PrimengButtonDirective,
    PrimengSelectDirective,
    ReactiveFormsModule,
    Select,
    WarehouseSkuUsageStatusComponent,
  ],
  templateUrl: './warehouse-receipt-form-product-panel.component.html',
  styleUrl: './warehouse-receipt-form-product-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseReceiptFormProductPanelComponent implements OnInit {
  private readonly unitProvider = inject(QuantityUnitProviderService);
  /* prettier-ignore */
  private readonly productTypeProvider = inject(WarehouseProductTypeProviderService);
  /* prettier-ignore */
  private readonly warehouseOwnerDataSource = inject(WarehouseOwnerDataSourceService);
  /* prettier-ignore */
  private readonly brandDataSource = inject(AdminBrandSelectItemsProviderService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  /* prettier-ignore */
  public readonly productFormGroup = input.required<WarehouseReceiptProductFormGroup>();
  public readonly index = input.required<number>();
  public readonly total = input.required<number>();
  public readonly removeClickEvent = output<number>();

  protected readonly panelCount = this.panelCountSignal();
  protected readonly shouldRemove = this.shouldRemoveSignal();
  protected readonly ownerSelectItems$ = this.ownerSelectItems();
  protected readonly productTypeSelectItems$ = this.productTypeSelectItems();
  protected readonly valueOrDefault = valueOrDefault;
  protected readonly unitSelectItems$ = this.unitSelectItems();

  protected currentName$!: Observable<Nillable<string>>;
  protected currentSku$!: Observable<Nillable<string>>;
  protected brandSuggestionSelectItems: SelectItem<Guid>[] = [];

  public ngOnInit(): void {
    this.configureCurrentNameSource();
    this.configureCurrentSkuSource();
  }

  protected handleProductRemoveClick(): void {
    this.removeClickEvent.emit(this.index());
  }

  protected handleCompleteMethod(event: AutoCompleteCompleteEvent): void {
    this.brandDataSource
      .fetchCollectionAsSelectItems('name', event.query.toLowerCase())
      .subscribe((selectItems) => {
        this.brandSuggestionSelectItems = selectItems;
        this.changeDetectorRef.detectChanges();
      });
  }

  private configureCurrentNameSource(): void {
    const group = this.productFormGroup();

    this.currentName$ = group.controls.name.valueChanges.pipe(
      startWith(group.controls.name.value),
    );
  }

  private configureCurrentSkuSource(): void {
    const group = this.productFormGroup();

    this.currentSku$ = group.controls.sku.valueChanges.pipe(debounceTime(300));
  }

  private productTypeSelectItems(): Observable<
    SelectItem<WarehouseProductType>[]
  > {
    return this.productTypeProvider.selectItems();
  }

  private unitSelectItems(): Observable<SelectItem<Guid>[]> {
    return this.unitProvider.selectItems();
  }

  private ownerSelectItems(): Observable<SelectItem<Guid>[]> {
    return this.warehouseOwnerDataSource.fetchCollectionAsSelectItems();
  }

  private panelCountSignal(): Signal<string> {
    return computed(() => `#${this.index() + 1}`);
  }

  private shouldRemoveSignal(): Signal<boolean> {
    return computed(() => this.total() !== 1);
  }
}
